import React, { useMemo, useState } from 'react';
import { trainingHistory, vendors } from 'Data/Kontak/Training';
import {
  Box,
  Typography,
  Card,
  CardHeader,
  CardContent,
  Divider,
  Grid,
  Avatar,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Link,
  TablePagination,
  TextField
} from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import { gridSpacing } from 'config.js';

const PelatihanHistory = () => {
  const rows = useMemo(() => {
    // flatten trainingHistory into rows with employee info
    return trainingHistory.flatMap((h) =>
      (h.trainings || []).map((t) => ({ emp_id: h.emp_id, full_name: h.full_name, ...t }))
    );
  }, []);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getVendorName = (id) => {
    const v = vendors.find((x) => x.id === id);
    return v ? v.name : id;
  };

  return (
    <Grid container spacing={gridSpacing}>
      <Grid item xs={12}>
        <Card
          sx={{
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
            transition: 'box-shadow 0.3s ease-in-out',
            '&:hover': { boxShadow: '0 12px 48px rgba(0,0,0,0.12)' }
          }}
          className="shadow-xl hover:shadow-2xl transition-shadow duration-300"
        >
          <CardHeader
            title={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }} className="shadow-lg">
                  <DescriptionIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.primary' }} className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                    History Pelatihan
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                    Daftar riwayat pelatihan peserta
                  </Typography>
                </Box>
              </Box>
            }
            action={
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<RefreshIcon />}
                  sx={{ borderRadius: 2, px: 2, py: 1.5, textTransform: 'none', fontWeight: 600 }}
                  onClick={() => { /* static data */ }}
                >
                  Refresh
                </Button>
              </Box>
            }
            sx={{ pb: 2 }}
          />

          <Divider />

          <CardContent>
            <Box sx={{ mb: 2 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Search by name or emp id"
                    InputProps={{ endAdornment: <SearchIcon /> }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box display="flex" justifyContent={{ xs: 'flex-start', md: 'flex-end' }} gap={1}>
                    <Button variant="contained" color="primary" startIcon={<SearchIcon />}>Cari</Button>
                    <Button variant="outlined">Reset</Button>
                  </Box>
                </Grid>
              </Grid>
            </Box>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{
                    bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'grey.50',
                    '& .MuiTableCell-head': { fontWeight: 600, color: 'text.primary', borderBottom: '2px solid', borderColor: 'primary.main' }
                  }}>
                    <TableCell sx={{ py: 2, px: 2, fontWeight: 600 }}>No</TableCell>
                    <TableCell sx={{ py: 2, px: 2, fontWeight: 600 }}>Employee</TableCell>
                    <TableCell sx={{ py: 2, px: 2, fontWeight: 600 }}>Training</TableCell>
                    <TableCell sx={{ py: 2, px: 2, fontWeight: 600 }}>Vendor</TableCell>
                    <TableCell sx={{ py: 2, px: 2, fontWeight: 600 }}>Date</TableCell>
                    <TableCell sx={{ py: 2, px: 2, fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ py: 2, px: 2, fontWeight: 600 }}>Certificate</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((r, idx) => (
                    <TableRow key={`${r.emp_id}-${idx}`}>
                      <TableCell sx={{ py: 2, px: 2 }}>{page * rowsPerPage + idx + 1}</TableCell>
                      <TableCell sx={{ py: 2, px: 2 }}>{r.full_name} ({r.emp_id})</TableCell>
                      <TableCell sx={{ py: 2, px: 2 }}>{r.title}</TableCell>
                      <TableCell sx={{ py: 2, px: 2 }}>{getVendorName(r.vendor_id)}</TableCell>
                      <TableCell sx={{ py: 2, px: 2 }}>{r.date_taken}</TableCell>
                      <TableCell sx={{ py: 2, px: 2 }}>{r.status}</TableCell>
                      <TableCell sx={{ py: 2, px: 2 }}>{r.certificate ? <Link href={r.certificate} target="_blank" rel="noreferrer">View</Link> : '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={rows.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default PelatihanHistory;
